import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { WebComponents } from "@/interfaces";
import { useAccountSettings, useSocialCoachSession } from "@/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppSetting, AppSettingServices, AppSettingPermissionUpdate } from "@/services/bedrock";
import _ from "lodash";
import pluralize from "pluralize";
import PlayerPermissionsModal from "./PlayerPermissionsModal";
import { Button, Switch, Popover, Spinner } from "@/components/ui";
import { AlertTriangle } from "lucide-react";

interface OwnProps {}
type Props = OwnProps & RouteComponentProps;

const PlatformSettingsTabFC: React.FC<Props> = ({}) => {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();

  const withWritePermission = hasWritePermission(WebComponents.APP_SETTINGS);
  const [loading, setLoading] = useState<boolean>(true);

  const [permissions, setPermissions] = useState<{ [key: string]: boolean | null }>({});
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [settingForPlayerPermissionsModal, setSettingForPlayerPermissionsModal] = useState<AppSetting>();

  const loadSettings = () => {
    setLoading(true);
    AppSettingServices.getAppSettings({ extraFields: ["currentUserPermission", "playerPermissionsCount"] })
      .then(response => {
        setSettings(response.content);
        const permissionEntries = response.content.map(setting => [
          setting.id,
          setting.currentUserPermission?.value ?? null,
        ]);
        const permissions = Object.fromEntries(permissionEntries);
        setPermissions(permissions);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(loadSettings, []);

  const permissionUpdates = useMemo(() => {
    if (!coachId) return [];

    return Object.keys(permissions)
      .map(appSettingId => {
        const existingPermission =
          settings.find(setting => setting.id === appSettingId)?.currentUserPermission?.value ?? null;
        if (existingPermission === permissions[appSettingId]) {
          return undefined;
        } else {
          return {
            appSettingId,
            accountId: coachId,
            value: permissions[appSettingId] ?? null,
          };
        }
      })
      .filter(Boolean) as AppSettingPermissionUpdate[];
  }, [settings, permissions, coachId]);

  const appSettingGroups = useMemo(() => _.groupBy(settings, "groupName"), [settings]);
  const saveSettings = useCallback(() => {
    if (!coachId) return;
    if (permissionUpdates.length === 0) return;

    const settingsOK = appSettingGroups["SOCIAL_NETWORKS"].some(setting => {
      if (!setting.id.startsWith("POST_IN")) return false;
      const permissionUpdate = permissionUpdates.find(update => update.appSettingId === setting.id);
      return permissionUpdate?.value ?? setting.defaultValue;
    });
    if (!settingsOK) {
      alert("At least one of the social network toggles should remain enabled.");
      return;
    }

    setLoading(true);
    AppSettingServices.updateCoachSettingPermissions(permissionUpdates)
      .then(loadSettings)
      .catch(error => {
        console.error(error);
        setLoading(false);
      });
  }, [permissionUpdates, coachId, appSettingGroups]);

  let groupNames = Object.keys(appSettingGroups);
  let colCutoff = Math.floor(groupNames.length / 2);
  let col1GroupNames = groupNames.slice(0, colCutoff);
  let col2GroupNames = groupNames.slice(colCutoff, groupNames.length);

  const fieldGroup = (groupName: string) => {
    const groupSettings = appSettingGroups[groupName];
    if (!groupSettings || groupSettings.length === 0) return null;

    return (
      <div
        key={`app-settings-group-${groupName}`}
        className="flex flex-col gap-2 p-4 border border-gray-200/60 rounded-xl bg-gray-50/30"
      >
        <label className="text-xs font-bold text-foreground uppercase tracking-wider">
          {_.startCase(_.capitalize(groupName))}
        </label>
        <div className="flex flex-col gap-2">
          {groupSettings.map(setting => {
            const isChecked = permissions[setting.id] ?? setting.defaultValue;
            const showWarning = setting.isPlayerSetting && (setting.playerPermissionsCount ?? 0) > 0;
            return (
              <div key={setting.id} className="flex items-center justify-start gap-2.5 py-0.5">
                <Switch
                  id={setting.id}
                  disabled={loading || !withWritePermission}
                  checked={isChecked}
                  onCheckedChange={checked => setPermissions({ ...permissions, [setting.id]: checked })}
                />
                <div className="flex items-center gap-1.5 min-w-0">
                  <label
                    htmlFor={setting.id}
                    className="text-xs font-medium text-foreground select-none cursor-pointer leading-none"
                  >
                    {setting.name}
                  </label>
                  {showWarning && (
                    <Popover>
                      <Popover.Trigger>
                        <button
                          type="button"
                          className="p-1 rounded-full text-amber-500 hover:bg-amber-50 transition-colors cursor-pointer outline-none"
                          disabled={!!settingForPlayerPermissionsModal}
                        >
                          <AlertTriangle className="size-3.5 shrink-0" />
                        </button>
                      </Popover.Trigger>
                      <Popover.Content
                        side="top"
                        className="w-64 p-3 flex flex-col gap-2 rounded-lg shadow-md bg-background text-foreground text-xs border border-gray-100 z-50"
                      >
                        <p className="leading-normal m-0 font-semibold text-muted-foreground">
                          There {pluralize("is", setting.playerPermissionsCount)}{" "}
                          <span className="font-bold text-foreground">{setting.playerPermissionsCount}</span>{" "}
                          {pluralize("Player", setting.playerPermissionsCount)} overriding this setting.
                        </p>
                        <Button
                          size="xs"
                          className="font-bold text-[10px] h-6 self-end px-3"
                          onClick={() => setSettingForPlayerPermissionsModal(setting)}
                        >
                          View List
                        </Button>
                      </Popover.Content>
                    </Popover>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handlePlayerPermissionsModalClose = (didSave: boolean) => {
    setSettingForPlayerPermissionsModal(undefined);
    if (didSave) loadSettings();
  };

  return (
    <div className="w-full text-left">
      <PlayerPermissionsModal
        setting={settingForPlayerPermissionsModal}
        withWritePermission={withWritePermission}
        onClose={handlePlayerPermissionsModalClose}
      />
      {loading && settings.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <Spinner className="size-8" />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-6">{col1GroupNames.map(groupName => fieldGroup(groupName))}</div>
            <div className="flex flex-col gap-6">{col2GroupNames.map(groupName => fieldGroup(groupName))}</div>
          </div>
          {withWritePermission && (
            <div className="flex pt-4 border-t border-gray-100">
              <Button
                disabled={permissionUpdates.length === 0 || loading}
                onClick={saveSettings}
                className="font-bold min-w-24 h-9"
              >
                {loading && <Spinner className="mr-2 size-3.5" />}
                Save
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const PlatformSettingsTab = withRouter(PlatformSettingsTabFC);
